'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/app/homepage/components/Footer';

export default function ProductsPage() {
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Full-width Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="relative w-full h-full bg-gradient-to-br from-green-900 to-green-700">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1612170153139-6f881ff067e0?w=1920')] bg-cover bg-center opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-green-900/90 via-green-800/80 to-green-900/90" />
          </div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center pt-32 pb-20 px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2.5 mb-8 reveal">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="text-sm font-semibold text-white">Premium Poultry Nutrition Solutions</span>
          </div>

          <h1 className="text-heading-1 md:text-[3.75rem] lg:text-[4.5rem] font-bold tracking-tight leading-[1.1] text-white mb-6 reveal">
            Our Dutch-formulated concentrates are designed for both Layer and Broiler success.
          </h1>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl max-w-4xl mx-auto reveal" style={{ animationDelay: '0.2s' }}>
            <div className="aspect-[16/9] bg-gradient-to-br from-green-100 to-green-50">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_146d671e7-1766824099694.png"
                alt="Brown layer hens in modern poultry farm with organized nesting boxes and fresh eggs"
                className="w-full h-full object-cover" />
              
            </div>
          </div>
        </div>
      </section>

      {/* Layer Concentrate Section */}
      <section className="py-section px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative reveal">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="aspect-[4/3] bg-gradient-to-br from-green-100 to-green-50">
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_146d671e7-1766824099694.png"
                    alt="Brown layer hens in modern poultry farm with organized nesting boxes and fresh eggs"
                    className="w-full h-full object-cover" />
                  
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8 reveal" style={{ animationDelay: '0.1s' }}>
              <div>
                <h2 className="text-heading-2 md:text-[3rem] text-foreground mb-4">
                  Bloom Concentrate for Layer Hens
                </h2>
                <p className="text-2xl text-primary font-semibold">Maximize Egg Production</p>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Product Overview</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  A premium poultry feed concentrate formulated to increase egg yield, improve shell strength, enhance bird immunity, and maintain consistent laying cycles.
                </p>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Key Benefits</h3>
                <ul className="space-y-3">
                  {[
                  'Higher egg production rates',
                  'Stronger eggshell quality',
                  'Improved overall bird health',
                  'Better laying consistency'].
                  map((benefit, index) =>
                  <li key={`layer_benefit_${index}`} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="CheckIcon" size={16} className="text-primary" />
                      </div>
                      <span className="text-base text-muted-foreground">{benefit}</span>
                    </li>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Recommended Use</h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  To be mixed with locally available energy sources (e.g., maize bran) according to provided feeding guidelines.
                </p>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Ideal For</h3>
                <ul className="space-y-2">
                  {[
                  'Commercial egg producers',
                  'Medium-scale poultry farms',
                  'Poultry cooperatives'].
                  map((customer, index) =>
                  <li key={`layer_customer_${index}`} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                      <span className="text-base text-muted-foreground">{customer}</span>
                    </li>
                  )}
                </ul>
              </div>

              <div className="pt-4">
                <p className="text-sm font-semibold text-muted-foreground mb-4">Packaging: Export-grade bags</p>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl">
                  
                  <span>Contact Us for Bulk Pricing</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Broiler Concentrate Section */}
      <section className="py-section px-6 bg-gradient-to-b from-white to-green-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-8 reveal lg:order-1">
              <div>
                <h2 className="text-heading-2 md:text-[3rem] text-foreground mb-4">
                  Bloom Concentrate for Broiler Hens
                </h2>
                <p className="text-2xl text-primary font-semibold">Accelerate Growth Performance</p>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Product Overview</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  A high-performance concentrate developed to support rapid growth, efficient feed conversion, optimal muscle development, and reduced mortality.
                </p>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Key Benefits</h3>
                <ul className="space-y-3">
                  {[
                  'Faster weight gain',
                  'Improved Feed Conversion Ratio (FCR)',
                  'Strong immune support',
                  'Increased profitability'].
                  map((benefit, index) =>
                  <li key={`broiler_benefit_${index}`} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="CheckIcon" size={16} className="text-primary" />
                      </div>
                      <span className="text-base text-muted-foreground">{benefit}</span>
                    </li>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Recommended Use</h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  To be mixed with local feed ingredients according to recommended formulation ratios.
                </p>
              </div>

              <div>
                <h3 className="text-heading-4 text-foreground mb-4">Ideal For</h3>
                <ul className="space-y-2">
                  {[
                  'Broiler meat producers',
                  'Commercial poultry farms',
                  'Contract farming operations'].
                  map((customer, index) =>
                  <li key={`broiler_customer_${index}`} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                      <span className="text-base text-muted-foreground">{customer}</span>
                    </li>
                  )}
                </ul>
              </div>

              <div className="pt-4">
                <p className="text-sm font-semibold text-muted-foreground mb-4">Packaging: Export-grade bags</p>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl">
                  
                  <span>Speak to a Sales Representative</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </a>
              </div>
            </div>

            {/* Image */}
            <div className="relative reveal lg:order-2" style={{ animationDelay: '0.1s' }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="aspect-[4/3] bg-gradient-to-br from-green-100 to-green-50">
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_17bb78f5f-1766982532254.png"
                    alt="Healthy white broiler chickens in clean farm environment showing optimal growth"
                    className="w-full h-full object-cover" />
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Dutch Formulation Section */}
      <section className="py-section px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
              Why Dutch Formulation <br />
              <span className="gradient-text">Matters</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Manufactured in the Netherlands using advanced feed technology and decades of poultry nutrition expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
            {
              id: 'feature_holland',
              icon: 'GlobeAltIcon',
              title: 'Manufactured in Holland',
              description: 'Produced under strict European quality control systems.'
            },
            {
              id: 'feature_scientific',
              icon: 'BeakerIcon',
              title: 'Scientifically Balanced',
              description: 'Precision nutrient profiles developed through research and field testing.'
            },
            {
              id: 'feature_consistency',
              icon: 'ChartBarIcon',
              title: 'Batch Consistency',
              description: 'Reliable performance across every production cycle.'
            },
            {
              id: 'feature_standards',
              icon: 'ShieldCheckIcon',
              title: 'European Standards',
              description: 'Meets strict EU feed safety regulations.'
            }].
            map((feature, index) =>
            <div
              key={feature.id}
              className="glass-card rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 reveal"
              style={{ animationDelay: `${index * 0.1}s` }}>
              
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <Icon name={feature.icon} size={28} className="text-primary" />
                </div>
                <h3 className="text-heading-4 text-foreground mb-3">{feature.title}</h3>
                <p className="text-base text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" className="py-section px-6 bg-gradient-to-b from-white to-green-50/30">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card rounded-3xl p-12 md:p-16 text-center reveal">
            <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
              Ready to Order <br />
              <span className="gradient-text">Premium Feed?</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Contact us for wholesale pricing, distributor opportunities, or to find a supplier near you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="#contact"
                className="group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl">
                
                <span>Request Wholesale Pricing</span>
                <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-3 glass-card px-8 py-4 rounded-full text-base font-bold hover:bg-white transition-all">
                
                <Icon name="MapPinIcon" size={20} className="text-primary" />
                <span>Find a Distributor</span>
              </a>
            </div>

            <div className="pt-8 border-t border-border">
              <p className="text-sm font-semibold text-muted-foreground mb-4">Need assistance? Contact our sales team:</p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-base">
                <a href="tel:0777014115" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium">
                  <Icon name="PhoneIcon" size={18} className="text-primary" />
                  <span>0777 014 115</span>
                </a>
                <span className="text-muted-foreground">|</span>
                <a href="tel:0704846600" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium">
                  <Icon name="PhoneIcon" size={18} className="text-primary" />
                  <span>0704 846 600</span>
                </a>
                <span className="text-muted-foreground">|</span>
                <a href="mailto:info@bloomanimalfeeds.com" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium">
                  <Icon name="EnvelopeIcon" size={18} className="text-primary" />
                  <span>info@bloomanimalfeeds.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>);

}